import {useEffect, useState} from 'react';
import {useMutation} from '@apollo/client';
import {Tag, Spin} from 'antd';
import moment from 'moment';
import {toast} from 'react-toastify';
import {
  CheckSquareOutlined,
  CloseCircleOutlined,
  FileOutlined,
  FileTextOutlined,
  MessageOutlined,
  PhoneOutlined,
  SoundOutlined,
} from '@ant-design/icons';
import {PRE_SIGN_MEDIA_URL} from 'graphql/mutations/wallet';
import {fullyDecodeURL} from 'utils/helper';
import DBModal from 'components/modal/modal';
import InteractionActionDetailModal from '../overview/sections/InteractionActionDetailModal';
import styles from './interactions.module.scss';

const formatDuration = ms => {
  if (ms == null) return '';
  const sec = Math.floor(ms / 1000);
  const min = Math.floor(sec / 60);
  if (min > 0) return `${min}:${String(sec % 60).padStart(2, '0')}`;
  return `${sec}s`;
};

const parseS3Url = url => {
  if (!url || typeof url !== 'string' || !url.includes('amazonaws.com'))
    return null;
  try {
    const parsed = new URL(url);
    const bucket = parsed.hostname.split('.')[0];
    const filename = decodeURIComponent(parsed.pathname).replace(/^\//, '');
    return bucket && filename ? {bucket, filename} : null;
  } catch {
    const bucket = url.split('.s3')[0]?.replace(/^https?:\/\//, '');
    const filename = url.split('amazonaws.com/')[1];
    return bucket && filename ? {bucket, filename} : null;
  }
};

const isPublicUrl = url => {
  if (!url) return false;
  return (
    url.startsWith('http') &&
    !url.includes('amazonaws.com') &&
    !url.includes('.s3.')
  );
};

const STATUS_COLORS = {
  not_started: 'default',
  in_progress: 'processing',
  done: 'success',
  cancelled: 'error',
};

const STATUS_LABELS = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  done: 'Completed',
  cancelled: 'Cancelled',
};

const InteractionDetailModal = ({isOpen, onClose, interaction, onRefetch}) => {
  const [presignedUrls, setPresignedUrls] = useState({});
  const [loadingFileIndex, setLoadingFileIndex] = useState(null);
  const [loadingAudioIndexes, setLoadingAudioIndexes] = useState({});
  const [selectedActionItem, setSelectedActionItem] = useState(null);
  const [presignMediaURL, {loading: presignLoading}] =
    useMutation(PRE_SIGN_MEDIA_URL);

  const files = interaction?.files ?? [];

  const presignFile = async file => {
    const fileUrl = file?.url;
    if (!fileUrl) return null;
    if (isPublicUrl(fileUrl)) return fileUrl;

    const parsed = parseS3Url(fileUrl);
    if (!parsed) {
      toast.error('Invalid file URL format');
      return null;
    }

    try {
      const {data} = await presignMediaURL({
        variables: {
          input: {
            filename: fullyDecodeURL(parsed.filename.split('?')[0]),
            bucket: parsed.bucket,
          },
        },
      });

      const result = data?.presignMediaURL;
      if (result?.__typename === 'Error') {
        toast.error(result.message);
        return null;
      }
      const presignedUrl = result?.data?.url ?? result?.data;
      return typeof presignedUrl === 'string' ? presignedUrl : null;
    } catch {
      toast.error('Failed to load file');
      return null;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setPresignedUrls({});
      setLoadingFileIndex(null);
      setLoadingAudioIndexes({});
      return;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !interaction || files.length === 0) return;

    const loadAudioUrls = async () => {
      const audioIndexes = {};
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const isAudio =
          file?.type && String(file.type).toLowerCase() === 'audio';
        if (isAudio && file?.url && !isPublicUrl(file.url)) {
          audioIndexes[i] = true;
        }
      }
      setLoadingAudioIndexes(audioIndexes);

      const results = {};
      for (const i of Object.keys(audioIndexes)) {
        const idx = Number(i);
        const presigned = await presignFile(files[idx]);
        if (presigned) results[idx] = presigned;
      }
      setPresignedUrls(prev => ({...prev, ...results}));
      setLoadingAudioIndexes({});
    };

    loadAudioUrls();
  }, [isOpen, interaction?.id]);

  if (!interaction) return null;

  const {
    type,
    date,
    note,
    summary,
    sentimentLabel,
    sentimentScore,
    transcript = [],
    actionItems = [],
  } = interaction;

  const sentimentColor =
    sentimentScore != null
      ? sentimentScore >= 0.5
        ? 'green'
        : sentimentScore >= 0
        ? 'gold'
        : 'red'
      : 'default';

  const priorityColors = {
    HIGH: 'red',
    MEDIUM: 'gold',
    LOW: 'default',
  };

  const getFileUrl = (file, index) => {
    if (!file?.url) return null;
    if (isPublicUrl(file.url)) return file.url;
    return presignedUrls[index] ?? null;
  };

  const handleViewFile = async (file, index) => {
    const fileUrl = file?.url;
    if (!fileUrl) return;
    if (isPublicUrl(fileUrl)) {
      window.open(fileUrl, '_blank');
      return;
    }

    setLoadingFileIndex(index);
    const presigned = presignedUrls[index] ?? (await presignFile(file));
    setLoadingFileIndex(null);
    if (presigned) {
      setPresignedUrls(prev => ({...prev, [index]: presigned}));
      window.open(presigned, '_blank');
    }
  };

  return (
    <>
      <DBModal
        isOpen={isOpen}
        handleClose={onClose}
        width={720}
        destroyOnClose
        content={
          <div className={styles.detailModal}>
            <div className={styles.detailHeader}>
              <Tag color="blue" className={styles.typeTag}>
                {type === 'CALL' && <PhoneOutlined />}
                {type === 'TEXT' && <MessageOutlined />}
                {type === 'BOTH' && (
                  <>
                    <MessageOutlined /> <PhoneOutlined />
                  </>
                )}
                {type === 'NO_ANSWER' && <CloseCircleOutlined />}
                <span>{type?.replace(/_/g, ' ')}</span>
              </Tag>
              <span className={styles.detailDate}>
                {date ? moment(date).format('DD MMM YYYY, h:mm A') : ''}
              </span>
            </div>

            <div className={styles.detailSections}>
              <section className={styles.detailSection}>
                <h3 className={styles.sectionTitle}>
                  <FileTextOutlined /> Notes
                </h3>
                <div className={styles.sectionContent}>
                  <p className={styles.noteText}>{note || 'No notes'}</p>
                </div>
              </section>

              {summary && (
                <section className={styles.detailSection}>
                  <h3 className={styles.sectionTitle}>
                    <FileTextOutlined /> Summary
                  </h3>
                  <div className={styles.sectionContent}>
                    <p className={styles.summaryText}>{summary}</p>
                  </div>
                </section>
              )}

              {(sentimentLabel != null || sentimentScore != null) && (
                <section className={styles.detailSection}>
                  <h3 className={styles.sectionTitle}>
                    <SoundOutlined /> Sentiment
                  </h3>
                  <div className={styles.sectionContent}>
                    <div className={styles.sentimentRow}>
                      {sentimentLabel && (
                        <Tag color={sentimentColor}>{sentimentLabel}</Tag>
                      )}
                      {sentimentScore != null && (
                        <span className={styles.sentimentScore}>
                          Score: {(sentimentScore * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {Array.isArray(transcript) && transcript.length > 0 && (
                <section className={styles.detailSection}>
                  <h3 className={styles.sectionTitle}>
                    <MessageOutlined /> Transcript
                  </h3>
                  <div className={styles.transcriptList}>
                    {transcript.map((item, idx) => (
                      <div
                        key={idx}
                        className={`${styles.transcriptItem} ${
                          item.speaker?.toLowerCase() === 'agent'
                            ? styles.agentBubble
                            : styles.customerBubble
                        }`}>
                        <div className={styles.transcriptMeta}>
                          <span className={styles.transcriptSpeaker}>
                            {item.speaker || 'Unknown'}
                          </span>
                          {(item.startTime != null ||
                            item.durationMs != null) && (
                            <span className={styles.transcriptTime}>
                              {item.startTime != null &&
                                formatDuration(item.startTime)}
                              {item.durationMs != null &&
                                ` · ${formatDuration(item.durationMs)}`}
                            </span>
                          )}
                        </div>
                        <p className={styles.transcriptText}>
                          {item.text || '—'}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {files.length > 0 && (
                <section className={styles.detailSection}>
                  <h3 className={styles.sectionTitle}>
                    <FileOutlined /> Files
                  </h3>
                  <div className={styles.filesList}>
                    {files.map((file, idx) => {
                      const isAudio =
                        file?.type &&
                        String(file.type).toLowerCase() === 'audio/wav';
                      const playableUrl = getFileUrl(file, idx);

                      return (
                        <div key={idx} className={styles.fileItem}>
                          {isAudio ? (
                            <div className={styles.audioPlayer}>
                              <span className={styles.fileName}>
                                {file.name || 'Audio'}
                              </span>
                              {playableUrl ? (
                                <audio
                                  controls
                                  src={playableUrl}
                                  className={styles.audio}>
                                  Your browser does not support the audio
                                  element.
                                </audio>
                              ) : (
                                <div className={styles.audioPlaceholder}>
                                  {loadingAudioIndexes[idx] ||
                                  loadingFileIndex === idx ? (
                                    <>
                                      <Spin size="small" />
                                      <span className={styles.loadingText}>
                                        Loading audio...
                                      </span>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      className={styles.loadAudioBtn}
                                      disabled={presignLoading}
                                      onClick={async () => {
                                        setLoadingFileIndex(idx);
                                        const url = await presignFile(file);
                                        if (url)
                                          setPresignedUrls(prev => ({
                                            ...prev,
                                            [idx]: url,
                                          }));
                                        setLoadingFileIndex(null);
                                      }}>
                                      Load audio
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : isPublicUrl(file?.url) || presignedUrls[idx] ? (
                            <a
                              href={
                                isPublicUrl(file?.url)
                                  ? file.url
                                  : presignedUrls[idx]
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.fileLink}>
                              <FileOutlined /> {file?.name || 'File'}
                            </a>
                          ) : (
                            <button
                              type="button"
                              className={styles.fileLinkButton}
                              disabled={
                                loadingFileIndex === idx || presignLoading
                              }
                              onClick={() => handleViewFile(file, idx)}>
                              {loadingFileIndex === idx ? (
                                <>
                                  <Spin size="small" />
                                  <span>Loading...</span>
                                </>
                              ) : (
                                <>
                                  <FileOutlined /> {file?.name || 'File'}
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {Array.isArray(actionItems) && actionItems.length > 0 && (
                <section className={styles.detailSection}>
                  <h3 className={styles.sectionTitle}>
                    <CheckSquareOutlined /> Action Items
                  </h3>
                  <div className={styles.actionItemsList}>
                    {actionItems.map((item, idx) => (
                      <div
                        key={idx}
                        className={`${styles.actionItemCard} ${styles.actionItemCardClickable}`}
                        onClick={() =>
                          setSelectedActionItem({
                            ...item,
                            interactionId: interaction?.id,
                          })
                        }>
                        <div className={styles.actionItemHeader}>
                          <span className={styles.actionItemTitle}>
                            {item.title || 'Untitled'}
                          </span>
                          <div className={styles.actionItemTags}>
                            <Tag
                              color={STATUS_COLORS[item.status] || 'default'}>
                              {STATUS_LABELS[item.status] || 'Not Started'}
                            </Tag>
                            {item.priority && (
                              <Tag
                                color={
                                  priorityColors[item.priority] || 'default'
                                }
                                className={styles.priorityTag}>
                                {item.priority}
                              </Tag>
                            )}
                          </div>
                        </div>
                        {item.description && (
                          <p className={styles.actionItemDesc}>
                            {item.description}
                          </p>
                        )}
                        <div className={styles.actionItemMeta}>
                          {item.category && (
                            <span className={styles.actionItemCategory}>
                              {item.category}
                            </span>
                          )}
                          {item.dueDate && (
                            <span className={styles.actionItemDue}>
                              Due: {moment(item.dueDate).format('DD MMM YYYY')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        }
      />
      <InteractionActionDetailModal
        isOpen={!!selectedActionItem}
        onClose={() => setSelectedActionItem(null)}
        actionItem={selectedActionItem}
        onStatusUpdated={() => {
          setSelectedActionItem(null);
          if (onRefetch) onRefetch();
        }}
      />
    </>
  );
};

export default InteractionDetailModal;
